import {
  getContractManagementContractFile,
  searchContractFileExtractions,
  getContractManagementContractFileClauses,
  getContractManagementContractFileObligations,
  getContractManagementContractFileDates,
  type ContractManagementContractFileInDbExtended,
  type ContractManagementExtractionSearchResult,
  type FilterableSearchQuery,
  type ContractManagementClauseInDb,
  type ContractManagementObligationInDb,
  type ContractManagementDateInDb,
} from '@/api';

/**
 * Extract error message from various error types
 */
function extractErrorMessage(err: unknown, defaultMessage: string): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'object' && err !== null && 'detail' in err) {
    const detail = err.detail;
    return String(detail);
  }
  return defaultMessage;
}

export const ContractFileDetailService = {
  /**
   * Get contract file by UID
   */
  async getContractFile(uid: string): Promise<ContractManagementContractFileInDbExtended> {
    try {
      const response = await getContractManagementContractFile({
        path: { uid },
      });

      if (!response.data) {
        throw new Error('Failed to fetch contract file');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch contract file. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Search extractions for a contract file with pagination and filtering
   */
  async searchExtractions(
    contractFileUid: string,
    query: {
      text?: string;
      page?: number;
      pageSize?: number;
      sortKey?: string;
      sortDirection?: 'asc' | 'desc';
      columnFilters?: Record<string, string[]>;
      revisionUid?: string | null;
    } = {}
  ): Promise<ContractManagementExtractionSearchResult> {
    try {
      const searchQuery: FilterableSearchQuery = {
        text: query.text ?? '',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        limit: query.pageSize || 10,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        offset: query.page ? (query.page - 1) * (query.pageSize || 10) : 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_key: query.sortKey || 'created_at',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_direction: query.sortDirection || 'desc',
        similarity_threshold: 0.5,
        column_filters: query.columnFilters ?? {},
      };

      const response = await searchContractFileExtractions({
        path: { uid: contractFileUid },
        body: searchQuery,
        query: query.revisionUid ? { revision_uid: query.revisionUid } : undefined,
      });

      if (!response.data) {
        throw new Error('Failed to fetch extractions');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch extractions. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Get obligations for a contract file
   */
  async getObligations(contractFileUid: string): Promise<ContractManagementObligationInDb[]> {
    try {
      const response = await getContractManagementContractFileObligations({
        path: { uid: contractFileUid },
      });

      if (!response.data) {
        throw new Error('Failed to fetch obligations');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch obligations. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Get obligations scoped to a specific vault file revision
   */
  async getObligationsByRevision(
    contractFileUid: string,
    revisionUid: string
  ): Promise<ContractManagementObligationInDb[]> {
    try {
      const response = await getContractManagementContractFileObligations({
        path: { uid: contractFileUid },
        query: { revision_uid: revisionUid },
      });
      if (!response.data) throw new Error('Failed to fetch obligations for revision');
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch obligations for revision. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Get clauses for a contract file
   */
  async getClauses(
    contractFileUid: string,
    revisionUid?: string | null
  ): Promise<ContractManagementClauseInDb[]> {
    try {
      const response = await getContractManagementContractFileClauses({
        path: { uid: contractFileUid },
        query: revisionUid ? { revision_uid: revisionUid } : undefined,
      });

      if (!response.data) {
        throw new Error('Failed to fetch clauses');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to fetch clauses. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Get dates for a contract file, optionally scoped to a vault file revision
   */
  async getDates(
    contractFileUid: string,
    revisionUid?: string | null
  ): Promise<ContractManagementDateInDb[]> {
    try {
      const response = await getContractManagementContractFileDates({
        path: { uid: contractFileUid },
        query: revisionUid ? { revision_uid: revisionUid } : undefined,
      });

      if (!response.data) {
        throw new Error('Failed to fetch dates');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to fetch dates. Please try again.'), {
        cause: error,
      });
    }
  },
};
